import { useEffect, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from '@/components/ui/dialog';
import { Plus, X } from 'lucide-react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

const STOCK_OPTIONS = [
  { v: 'in_stock', label: 'In stock' },
  { v: 'out_of_stock_coming_soon', label: 'Coming soon (out)' },
  { v: 'inactive', label: 'Inactive (hidden)' },
];

const INITIAL = {
  sku: '',
  upc: '',
  name: '',
  brand: '',
  category: '',
  sub_type: '',
  variant: '',
  description: '',
  features: '',            // one per line → list
  specifications: '',      // "key: value" per line → dict
  images: '',              // URLs one per line → list
  wholesale_price: '',
  msrp: '',
  case_pack_size: '1',
  min_order_qty: '1',
  price_breaks: '',        // "min_qty:unit_price" one per line
  stock_status: 'in_stock',
  stock_qty: '0',
  active: true,
  supplier: '',
  supplier_sku: '',
  supplier_discount_pct: '',
  supplier_cost: '',
};

function parseFeatures(s) {
  return s.split('\n').map((l) => l.trim()).filter(Boolean);
}
function parseSpecs(s) {
  const out = {};
  for (const raw of s.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const idx = line.indexOf(':');
    if (idx === -1) continue;
    const k = line.slice(0, idx).trim();
    const v = line.slice(idx + 1).trim();
    if (k) out[k] = v;
  }
  return out;
}
function parseImages(s) {
  return s.split('\n').map((l) => l.trim()).filter(Boolean);
}
function parseBreaks(s) {
  const out = [];
  for (const raw of s.split('\n')) {
    const line = raw.trim();
    if (!line) continue;
    const [q, p] = line.split(':').map((x) => x.trim());
    const min_qty = parseInt(q, 10);
    const unit_price = parseFloat(p);
    if (!Number.isNaN(min_qty) && !Number.isNaN(unit_price)) {
      out.push({ min_qty, unit_price });
    }
  }
  return out.sort((a, b) => a.min_qty - b.min_qty);
}

export default function AddProductDialog({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(INITIAL);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [suppliers, setSuppliers] = useState([]);

  useEffect(() => {
    if (!open) return;
    api.get('/admin/suppliers').then(({ data }) => setSuppliers(data || [])).catch(() => {});
  }, [open]);

  const upd = (k) => (e) => {
    const v = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setForm((cur) => {
      const next = { ...cur, [k]: v };
      // Auto-prefill vendor default when admin picks a known supplier and
      // hasn't already set a discount % or explicit cost for this item.
      if (k === 'supplier') {
        const match = suppliers.find(
          (s) => (s.name || '').toLowerCase() === (v || '').trim().toLowerCase(),
        );
        if (match && match.default_discount_pct != null && !cur.supplier_discount_pct && !cur.supplier_cost) {
          next.supplier_discount_pct = String(match.default_discount_pct);
        }
      }
      return next;
    });
  };

  const supplierMatch = suppliers.find(
    (s) => (s.name || '').toLowerCase() === (form.supplier || '').trim().toLowerCase(),
  );
  const supplierPrefilled = !!(
    supplierMatch &&
    supplierMatch.default_discount_pct != null &&
    Number(form.supplier_discount_pct) === Number(supplierMatch.default_discount_pct)
  );

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setBusy(true);
    try {
      const payload = {
        sku: form.sku.trim(),
        upc: form.upc.trim() || null,
        name: form.name.trim(),
        brand: form.brand.trim(),
        category: form.category.trim(),
        sub_type: form.sub_type.trim() || null,
        variant: form.variant.trim() || null,
        description: form.description.trim(),
        features: parseFeatures(form.features),
        specifications: parseSpecs(form.specifications),
        images: parseImages(form.images),
        wholesale_price: parseFloat(form.wholesale_price) || 0,
        msrp: parseFloat(form.msrp) || 0,
        case_pack_size: parseInt(form.case_pack_size, 10) || 1,
        min_order_qty: parseInt(form.min_order_qty, 10) || 1,
        price_breaks: parseBreaks(form.price_breaks),
        stock_status: form.stock_status,
        stock_qty: parseInt(form.stock_qty, 10) || 0,
        active: !!form.active,
        supplier: form.supplier.trim() || null,
        supplier_sku: form.supplier_sku.trim() || null,
        supplier_discount_pct: form.supplier_discount_pct === '' ? null : parseFloat(form.supplier_discount_pct),
        supplier_cost: form.supplier_cost === '' ? null : parseFloat(form.supplier_cost),
      };
      await api.post('/products', payload);
      toast.success(`Added ${payload.name}`);
      setForm(INITIAL);
      setOpen(false);
      onCreated && onCreated();
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button type="button" className="btn-primary" data-testid="admin-add-product">
          <Plus size={14} /> Add Product
        </button>
      </DialogTrigger>
      <DialogContent
        className="max-w-3xl max-h-[90vh] overflow-y-auto p-0 rounded-none border border-neutral-200 bg-white"
      >
        <DialogHeader className="p-6 border-b border-neutral-200 bg-black text-white">
          <DialogTitle className="serif text-2xl italic tracking-tight">Add a product</DialogTitle>
          <p className="sans text-xs text-neutral-400 tracking-widest uppercase mt-1">
            Any supplier · Any brand · Doc Johnson or partner label
          </p>
        </DialogHeader>

        <form onSubmit={submit} className="p-6 grid gap-5 md:grid-cols-2" data-testid="admin-add-product-form">
          <div className="md:col-span-2">
            <label className="ije-label">Product Name *</label>
            <input required data-testid="apf-name" className="ije-input" value={form.name} onChange={upd('name')} placeholder="e.g. Fun Factory Volita Vibrator" />
          </div>

          <div>
            <label className="ije-label">SKU *</label>
            <input required data-testid="apf-sku" className="ije-input" value={form.sku} onChange={upd('sku')} placeholder="Unique SKU" />
          </div>
          <div>
            <label className="ije-label">UPC / Barcode</label>
            <input data-testid="apf-upc" className="ije-input" value={form.upc} onChange={upd('upc')} placeholder="Optional" />
          </div>

          <div>
            <label className="ije-label">Brand / Supplier *</label>
            <input
              required data-testid="apf-brand" className="ije-input"
              value={form.brand} onChange={upd('brand')}
              placeholder="Any label — e.g. Fun Factory, We-Vibe, LELO, Classic…"
            />
            <p className="sans text-[0.65rem] text-neutral-500 mt-1">
              Free text — enter any partner brand, not just Doc Johnson labels.
            </p>
          </div>
          <div>
            <label className="ije-label">Category *</label>
            <input required data-testid="apf-category" className="ije-input" value={form.category} onChange={upd('category')} placeholder="e.g. Dildos, Lubricants…" />
          </div>

          <div>
            <label className="ije-label">Sub-type</label>
            <input data-testid="apf-subtype" className="ije-input" value={form.sub_type} onChange={upd('sub_type')} placeholder="Optional finer classification" />
          </div>
          <div>
            <label className="ije-label">Variant / Color</label>
            <input data-testid="apf-variant" className="ije-input" value={form.variant} onChange={upd('variant')} placeholder="e.g. Black, Vanilla…" />
          </div>

          <div className="md:col-span-2">
            <label className="ije-label">Description</label>
            <textarea data-testid="apf-description" rows={3} className="ije-input" value={form.description} onChange={upd('description')} />
          </div>

          <div className="md:col-span-2">
            <label className="ije-label">Features (one per line)</label>
            <textarea data-testid="apf-features" rows={3} className="ije-input" value={form.features} onChange={upd('features')} placeholder={'Body-safe silicone\nUSB rechargeable\nWaterproof'} />
          </div>

          <div className="md:col-span-2">
            <label className="ije-label">Specifications (one "key: value" per line)</label>
            <textarea data-testid="apf-specs" rows={3} className="ije-input" value={form.specifications} onChange={upd('specifications')} placeholder={'Material: Silicone\nTotal Length: 7 in\nInsertable: 6 in'} />
          </div>

          <div className="md:col-span-2">
            <label className="ije-label">Image URLs (one per line)</label>
            <textarea data-testid="apf-images" rows={2} className="ije-input" value={form.images} onChange={upd('images')} placeholder="https://…/image1.jpg" />
            <p className="sans text-[0.65rem] text-neutral-500 mt-1">
              Leave blank to use an IJE HAUS branded placeholder tile.
            </p>
          </div>

          <div>
            <label className="ije-label">Wholesale Price *</label>
            <input required type="number" step="0.01" min="0" data-testid="apf-wholesale" className="ije-input" value={form.wholesale_price} onChange={upd('wholesale_price')} placeholder="0.00" />
          </div>
          <div>
            <label className="ije-label">MSRP</label>
            <input type="number" step="0.01" min="0" data-testid="apf-msrp" className="ije-input" value={form.msrp} onChange={upd('msrp')} placeholder="0.00" />
          </div>

          <div className="md:col-span-2 border-t border-neutral-200 pt-4 mt-1">
            <p className="eyebrow">Vendor / supplier (private)</p>
            <p className="sans text-[11px] text-neutral-500 mt-0.5">Only admins see these fields — never shown to buyers. Used for margin calculations and reorder tracking.</p>
          </div>
          <div>
            <label className="ije-label">Vendor / Supplier</label>
            <input
              data-testid="apf-supplier"
              list="apf-supplier-suggest"
              className="ije-input"
              value={form.supplier}
              onChange={upd('supplier')}
              placeholder="e.g. Doc Johnson, NeuroSource, or new vendor"
            />
            <datalist id="apf-supplier-suggest">
              {suppliers.map((s) => (
                <option key={s.name} value={s.name}>{`${s.product_count} product${s.product_count === 1 ? '' : 's'}`}</option>
              ))}
            </datalist>
          </div>
          <div>
            <label className="ije-label">Vendor SKU</label>
            <input data-testid="apf-supplier-sku" className="ije-input" value={form.supplier_sku} onChange={upd('supplier_sku')} placeholder="Optional — the vendor's own SKU" />
          </div>
          <div>
            <label className="ije-label">% off wholesale</label>
            <input type="number" step="0.1" min="0" max="99" data-testid="apf-supplier-pct" className="ije-input" value={form.supplier_discount_pct} onChange={upd('supplier_discount_pct')} placeholder="e.g. 46" />
            <p className="sans text-[11px] text-neutral-500 mt-1">
              {supplierPrefilled
                ? `Auto-filled from ${supplierMatch.name}'s default (${supplierMatch.default_discount_pct}%). Edit to override.`
                : "Vendor's discount off IJE's wholesale price. Auto-fills cost below if left blank."}
            </p>
          </div>
          <div>
            <label className="ije-label">Vendor Cost $</label>
            <input type="number" step="0.01" min="0" data-testid="apf-supplier-cost" className="ije-input" value={form.supplier_cost} onChange={upd('supplier_cost')} placeholder="Leave blank to derive from % off" />
            <p className="sans text-[11px] text-neutral-500 mt-1">
              {form.supplier_discount_pct && form.wholesale_price && !form.supplier_cost
                ? `Will save as $${(Number(form.wholesale_price) * (1 - Number(form.supplier_discount_pct) / 100)).toFixed(2)}`
                : 'Your buy price. Used to compute margin against wholesale.'}
            </p>
          </div>

          <div className="md:col-span-2 border-t border-neutral-200 pt-4 mt-1">
            <p className="eyebrow">Inventory & pack</p>
          </div>
          <div>
            <label className="ije-label">Case Pack Size</label>
            <input type="number" min="1" data-testid="apf-case" className="ije-input" value={form.case_pack_size} onChange={upd('case_pack_size')} />
          </div>
          <div>
            <label className="ije-label">Min Order Qty</label>
            <input type="number" min="1" data-testid="apf-moq" className="ije-input" value={form.min_order_qty} onChange={upd('min_order_qty')} />
          </div>

          <div className="md:col-span-2">
            <label className="ije-label">Price Breaks (optional, one "min_qty: unit_price" per line)</label>
            <textarea data-testid="apf-breaks" rows={3} className="ije-input" value={form.price_breaks} onChange={upd('price_breaks')} placeholder={'12: 12.50\n60: 11.75\n144: 11.00'} />
          </div>

          <div>
            <label className="ije-label">Stock Status</label>
            <select data-testid="apf-stock-status" className="ije-input" value={form.stock_status} onChange={upd('stock_status')}>
              {STOCK_OPTIONS.map((o) => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </div>
          <div>
            <label className="ije-label">Stock Qty</label>
            <input type="number" min="0" data-testid="apf-stock-qty" className="ije-input" value={form.stock_qty} onChange={upd('stock_qty')} />
          </div>

          <label className="md:col-span-2 flex items-center gap-3 cursor-pointer">
            <input type="checkbox" data-testid="apf-active" checked={form.active} onChange={upd('active')} className="accent-[color:var(--ije-pink)]" />
            <span className="sans text-sm text-neutral-700">Active (visible in catalog)</span>
          </label>

          {error && (
            <div className="md:col-span-2 sans text-sm" style={{ color: 'var(--ije-pink-dark)' }} data-testid="apf-error">{error}</div>
          )}

          <DialogFooter className="md:col-span-2 flex-row gap-3 justify-end mt-2 pt-4 border-t border-neutral-200">
            <button type="button" onClick={() => setOpen(false)} className="btn-outline" data-testid="apf-cancel">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary" data-testid="apf-submit">
              {busy ? 'Saving…' : 'Add Product'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
